import {
  Component, OnInit, AfterViewInit, ViewChild, ElementRef, OnDestroy
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from 'rxjs';
import { Location } from "@angular/common";

import * as Communication from "../communication/index";
import { Config } from "../../environments/environment";
import { NavigationService } from "../shared/common/NavigationEventService";
import { NavigationEvent } from "../shared/common/NavigationEvent";
import { ToolbarService } from "../shared/common/ToolbarEventService";
import { ToolbarEventPageEnum, ToolbarEvent } from "../shared/common/ToolbarEvent";
import { NotificationService } from "../shared/common/NotificationService";
import { Authorization } from "../shared/authorization/authorization.component";
import { TranslateService } from '@ngx-translate/core';

// Import pdf-form-designer types and component
import {
  PdfFormDesignerComponent,
  FormConfiguration,
  FormElement,
  FormDesignerOptions
} from '../../../../libs/form-designer/projects/pdf-form-designer/src/public-api';

@Component({
  selector: 'sd-form-designer',
  templateUrl: 'form-designer.component.html',
  styleUrls: ['form-designer.component.css'],
  providers: [
    Communication.JobsService,
    Communication.DocumentsService,
    Communication.FieldsService,
    Communication.UsersService
  ],
  standalone: false
})
export class FormDesignerComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(PdfFormDesignerComponent) pdfFormDesigner!: PdfFormDesignerComponent;

  // Component state
  jobIDParameter: number;
  theJob: Communication.Job;
  pdfLoading: boolean = true;
  subscriptions: Subscription[] = [];
  docFields: any = [];
  changeOccured: boolean = false;
  suspendSave: boolean = false;
  toogleSidebar: boolean = false;

  // PDF source configuration
  pdfSrc: any = {
    url: '',
    httpHeaders: {
      access_token: "1"
    }
  };

  // Form designer configuration
  currentConfiguration?: FormConfiguration;
  selectedElement?: FormElement | null;

  // PDF viewer controls
  currentZoom: number = 1.0;
  currentRotation: number = 0;
  currentPage: number = 1;
  pageCount: number = 1;

  // Form designer options
  formDesignerOptions: FormDesignerOptions = {
    enableGrid: false,
    gridSize: 10,
    enableSnapping: true,
    snapTolerance: 5,
    enableMultiSelect: false,
    enableKeyboardShortcuts: true,
    readOnly: false,
    locale: this.translate.currentLang as 'en' | 'tr' | 'nl',
    toolbar: {
      enabled: true,
      external: true,
      containerId: 'external-toolbar-container',
      showLabels: true,
      showIcons: true,
      showSelectedInfo: false,
      showActions: false
    }
  };

  constructor(
    private jobsApi: Communication.JobsService,
    private documentsApi: Communication.DocumentsService,
    private fieldsApi: Communication.FieldsService,
    private usersApi: Communication.UsersService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private navigationService: NavigationService,
    private toolbarService: ToolbarService,
    private location: Location,
    private authorization: Authorization,
    private notificationService: NotificationService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    if (!this.authorization.authenticated()) {
      this.router.navigate(['/']);
      return;
    }

    // Set locale for form designer based on current language
    // Read language directly from localStorage to ensure we get the correct value
    let locale: 'en' | 'tr' | 'nl' = 'en'; // default
    try {
      const account = localStorage.getItem('account');
      if (account) {
        const accountData = JSON.parse(account);
        const accountLanguage = accountData.language;
        if (accountLanguage && ['en', 'tr', 'nl'].includes(accountLanguage)) {
          locale = accountLanguage as 'en' | 'tr' | 'nl';
        }
      }
    } catch (e) {
      console.warn('Could not read language from localStorage, using default:', e);
    }

    console.log('Form Designer - Setting locale to:', locale);
    console.log('Form Designer - translate.currentLang:', this.translate.currentLang);
    this.formDesignerOptions = {
      ...this.formDesignerOptions,
      locale: locale
    };
    console.log('Form Designer - Options:', this.formDesignerOptions);

    // Subscribe to navigation events
    this.subscriptions.push(
      this.navigationService.eventOccured$.subscribe(
        (event: NavigationEvent) => this.onNavigationEvent(event)
      )
    );

    // Get job ID from route parameters
    this.subscriptions.push(
      this.activatedRoute.params.subscribe(
        (param) => {
          this.jobIDParameter = +param['id'];
          console.log("Form Designer - Job ID:", this.jobIDParameter);
          this.getTheJob();
        }
      )
    );
  }

  ngAfterViewInit() {
    // Component view initialization if needed
  }

  ngOnDestroy() {
    // Cleanup subscriptions
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  /**
   * Get job details and document (following prepare-self pattern)
   */
  public getTheJob(): void {
    this.subscriptions.push(
      this.jobsApi.jobsIdGet(this.authorization.getAccessToken(), this.jobIDParameter).subscribe(
        (response: Communication.Job) => {
          this.theJob = response;
          console.log("Form Designer - Job:", response);

          // Load existing fields
          this.getDocFields();

          // Update toolbar with job info
          this.toolbarService.action(
            new ToolbarEvent(
              ToolbarEventPageEnum.DESIGN,
              response.type,
              response.user.id,
              "",
              response.name,
              response.status,
              "",
              "",
              ""
            )
          );

          // Load the first document
          if (response.documents && response.documents.length > 0) {
            const documentModel: Communication.Document = response.documents[0];
            const fileURL: string = Config.WEBSERVICEURL + '/documents/' + documentModel.id + '/download';
            const accessToken: string = this.authorization.getAccessToken();

            this.loadFile(fileURL, accessToken);

            console.log("job status: " + this.theJob.status + " - type: " + this.theJob.type);
            console.log("documentID: " + this.theJob.documents[0].id);
          } else {
            this.notificationService.documentNotFound();
          }
        },
        err => {
          console.error("Error loading job:", err);
          this.notificationService.displayError(err);
        }
      )
    );
  }

  /**
   * Load PDF file with authorization (following prepare-self pattern)
   */
  loadFile(fileURL: string, accessToken: string): void {
    const xhr: any = new XMLHttpRequest();
    xhr.open('HEAD', fileURL, false);
    xhr.setRequestHeader('access_token', accessToken);
    xhr.send();

    if (xhr.status == 200 || xhr.status == "200") {
      this.pdfSrc.url = fileURL;
      this.pdfSrc.httpHeaders.access_token = accessToken;
      this.pdfLoading = false;
    } else {
      this.notificationService.documentNotFound();
      this.pdfLoading = false;
    }
  }

  /**
   * Get existing document fields (following prepare-self pattern)
   */
  getDocFields(): void {
    if (!this.theJob?.documents?.[0]) {
      return;
    }

    this.subscriptions.push(
      this.fieldsApi.documentsIdFieldsGet(
        this.authorization.getAccessToken(),
        this.theJob.documents[0].id
      ).subscribe(
        (response) => {
          console.log("GET/docFields... existing fields:", response);

          const allFields: Array<Communication.DocumentField> = response;
          this.docFields = [];

          // Store fields in component state
          for (let i = 0; i < allFields.length; i++) {
            const fields: any = {
              id: i + 1,
              documentId: allFields[i].documentId,
              recipientId: allFields[i].recipientId,
              page: allFields[i].page,
              type: allFields[i].type,
              width: allFields[i].width,
              height: allFields[i].height,
              x: allFields[i].x,
              y: allFields[i].y,
              label: allFields[i].label,
              enabled: allFields[i].enabled,
              initialValue: allFields[i].initialValue,
              value: allFields[i].value,
              variable: allFields[i].variable,
              validation: allFields[i].validation,
              orderno: allFields[i].orderno,
              required: allFields[i].required,
              options: allFields[i].options
            };
            this.docFields.push(fields);
          }

          // Convert to FormConfiguration if fields exist
          if (this.docFields.length > 0) {
            const formElements = this.convertFieldsToFormElements(allFields);
            const configuration: FormConfiguration = {
              elements: formElements,
              pageCount: this.pageCount,
              version: '3.0.0',
              signers: [{
                id: 'default-signer',
                name: 'Default Signer',
                email: 'default@example.com',
                color: '#3B82F6'
              }],
              activeSignerId: 'default-signer',
              metadata: {
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                author: this.authorization.userProfile?.user_metadata?.name || 'Unknown',
                coordinateSystem: 'PDF_EXACT_POINTS' as const
              }
            };

            // Load configuration into pdf-form-designer after component initializes
            setTimeout(() => {
              if (this.pdfFormDesigner) {
                this.pdfFormDesigner.loadConfiguration(configuration);
              }
            }, 500);
          }
        },
        err => {
          console.error("Error loading fields:", err);
          this.notificationService.displayError(err);
        }
      )
    );
  }

  /**
   * Convert API fields to FormElement format
   */
  private convertFieldsToFormElements(fields: Communication.DocumentField[]): FormElement[] {
    return fields.map(field => {
      const element: any = {
        id: `field-${field.id || Math.random().toString(36).substr(2, 9)}`,
        type: this.mapFieldType(field.type),
        pageIndex: ((field.page as any) || 1) - 1, // Convert to 0-based index
        pdfPosition: {
          x: field.x || 0,
          y: field.y || 0,
          width: field.width || 100,
          height: field.height || 30
        },
        pageDimensions: {
          width: 612,  // US Letter width in PDF points
          height: 792  // US Letter height in PDF points
        },
        position: {
          x: field.x || 0,
          y: field.y || 0,
          width: field.width || 100,
          height: field.height || 30
        },
        name: field.label || field.variable || '',
        properties: this.mapFieldProperties(field)
      };

      return element;
    });
  }

  /**
   * Map field type from API to FormElement type
   */
  private mapFieldType(type: string): string {
    const typeMap: { [key: string]: string } = {
      'text-input': 'text-input',
      'text-area': 'text-input',
      'checkbox': 'checkbox',
      'radio': 'radio',
      'sign-area': 'signature',
      'bio-sign': 'signature',
      'initials': 'signature'
    };

    return typeMap[type] || 'text';
  }

  /**
   * Map field properties from API to FormElement properties
   */
  private mapFieldProperties(field: Communication.DocumentField): any {
    const properties: any = {
      required: field.required === 'true' || (field.required as any) === true,
      readOnly: field.enabled === 'false' || (field.enabled as any) === false,
      defaultValue: field.initialValue || field.value || ''
    };

    // Parse options if available
    if (field.options) {
      try {
        const options = JSON.parse(field.options);
        if (options.font) properties.fontFamily = options.font;
        if (options.size) properties.fontSize = options.size;
        if (options.color) properties.textColor = options.color;
      } catch (e) {
        console.warn("Could not parse field options:", field.options);
      }
    }

    // Type-specific properties
    switch (field.type) {
      case 'text-input':
      case 'text-area':
        properties.placeholder = field.label || '';
        break;
      case 'checkbox':
        properties.label = field.label || '';
        properties.checked = field.initialValue === 'true';
        break;
      case 'radio':
        properties.groupName = field.variable || 'radio-group';
        properties.value = field.label || '';
        properties.label = field.label || '';
        properties.checked = field.initialValue === 'true';
        break;
      case 'sign-area':
      case 'bio-sign':
      case 'initials':
        properties.backgroundColor = '#f8f9fa';
        properties.borderColor = '#dee2e6';
        break;
    }

    return properties;
  }

  /**
   * Handle form configuration changes
   */
  onConfigurationChange(config: FormConfiguration): void {
    this.currentConfiguration = config;
    this.pageCount = config.pageCount || 1;
    this.changeOccured = true;
    console.log('Form configuration updated:', config);

    // Auto-save configuration if not suspended
    if (!this.suspendSave) {
      this.saveConfiguration();
    }
  }

  /**
   * Save form configuration to API (following prepare-self saveShapes pattern)
   */
  saveConfiguration(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!this.currentConfiguration || !this.theJob?.documents?.[0]) {
        resolve();
        return;
      }

      // Convert FormElements to DocumentFields
      const documentFields = this.convertFormElementsToFields(
        this.currentConfiguration.elements
      );

      // Save to API
      const json: string = JSON.stringify(documentFields);
      this.subscriptions.push(
        this.fieldsApi.documentsIdFieldsPut(
          this.authorization.getAccessToken(),
          this.theJob.documents[0].id,
          json
        ).subscribe(
          (response: any) => {
            console.log("Form configuration saved");
            this.changeOccured = false;
            resolve();
          },
          (err: any) => {
            console.error("Error saving configuration:", err);
            this.notificationService.displayError(err);
            reject(err);
          }
        )
      );
    });
  }

  /**
   * Convert FormElements to API DocumentField format
   */
  private convertFormElementsToFields(elements: FormElement[]): any[] {
    return elements.map((element, index) => {
      const field: any = {
        documentId: this.theJob.documents[0].id,
        recipientId: this.theJob.recipients?.[0]?.id || null,
        page: (element.pageIndex || 0) + 1, // Convert to 1-based index
        type: this.mapFormElementTypeToField(element.type),
        width: element.pdfPosition?.width || element.position?.width || 100,
        height: element.pdfPosition?.height || element.position?.height || 30,
        x: element.pdfPosition?.x || element.position?.x || 0,
        y: element.pdfPosition?.y || element.position?.y || 0,
        label: element.name || '',
        enabled: !(element.properties as any)?.readOnly,
        initialValue: (element.properties as any)?.defaultValue || '',
        value: '',
        variable: element.name || `field-${index}`,
        validation: '',
        orderno: index,
        required: (element.properties as any)?.required || false,
        options: JSON.stringify(this.extractFieldOptions(element))
      };

      return field;
    });
  }

  /**
   * Map FormElement type to API field type
   */
  private mapFormElementTypeToField(type: string): string {
    const typeMap: { [key: string]: string } = {
      'text-input': 'text-input',
      'checkbox': 'checkbox',
      'radio': 'radio',
      'signature': 'sign-area',
      'text': 'text-area'
    };

    return typeMap[type] || 'text-input';
  }

  /**
   * Extract field options for API
   */
  private extractFieldOptions(element: FormElement): any {
    const options: any = {};
    const props = element.properties as any;

    if (props?.fontFamily) options.font = props.fontFamily;
    if (props?.fontSize) options.size = props.fontSize;
    if (props?.textColor) options.color = props.textColor;

    return options;
  }

  /**
   * Handle element selection
   */
  onElementSelected(element: FormElement | null): void {
    this.selectedElement = element;
    console.log('Element selected:', element);
  }

  /**
   * Handle element addition
   */
  onElementAdded(element: FormElement): void {
    console.log('Element added:', element);
  }

  /**
   * Handle element modification
   */
  onElementModified(element: FormElement): void {
    console.log('Element modified:', element);
  }

  /**
   * Handle element removal
   */
  onElementRemoved(element: FormElement): void {
    console.log('Element removed:', element);
  }

  /**
   * Handle navigation events
   */
  private onNavigationEvent(event: NavigationEvent): void {
    switch (event.acton) {
      case "CLOSE_REQUEST":
        this.closeRequest();
        break;
      case "FINISH_SEND_REQUEST":
        this.finishAndSend();
        break;
    }
  }

  /**
   * Close and return to previous page
   */
  closeRequest(): void {
    // Save before closing
    this.suspendSave = true;
    this.saveConfiguration().then(() => {
      setTimeout(() => {
        this.location.back();
      }, 100);
    }).catch(error => {
      console.error("Error saving before close:", error);
    });
  }

  /**
   * Finish and send the request
   */
  finishAndSend(): void {
    // Save configuration first
    this.suspendSave = true;
    this.saveConfiguration().then(() => {
      // Check if there are recipients
      if (!this.theJob.recipients || this.theJob.recipients.length === 0) {
        this.notificationService.noRecipientExist();
        return;
      }

      // Prepare subject and message
      let subject: string = "";
      let message: string = "";

      if (!this.theJob.subject) {
        subject = this.translate.currentLang == "tr"
          ? "'" + this.theJob.name + "' " + this.translate.instant("Approval required for")
          : this.translate.instant("Approval required for") + " '" + this.theJob.name + "'";
      } else {
        subject = this.theJob.subject;
      }

      if (!this.theJob.message) {
        message = this.translate.instant("Hello") + ",\n\n" +
          this.translate.instant("The related document has been sent for signature.") + "\n\n" +
          this.authorization.userProfile.user_metadata.name + "\n\n";
      } else {
        message = this.theJob.message;
      }

      // Send the request
      this.subscriptions.push(
        this.jobsApi.jobsIdActionsFinishPut(
          this.authorization.getAccessToken(),
          this.jobIDParameter,
          subject,
          message,
          "true"
        ).subscribe(
          response => {
            this.notificationService.signingRequestSent();
            console.log("Finish - status " + response.status);
            setTimeout(() => {
              this.location.back();
            }, 100);
          },
          err => {
            console.error(err);
            const errorObject = JSON.parse(err._body);
            if (errorObject.code == 265) {
              this.notificationService.smsQuotaIsFull();
            } else {
              this.notificationService.displayError(err);
            }
          }
        )
      );
    }).catch(error => {
      console.error("Error saving configuration:", error);
    });
  }

  /**
   * Export configuration
   */
  exportConfiguration(): void {
    if (this.currentConfiguration) {
      const configJson = JSON.stringify(this.currentConfiguration, null, 2);
      const blob = new Blob([configJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `form-config-job-${this.jobIDParameter}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Import configuration
   */
  importConfiguration(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const config = JSON.parse(e.target?.result as string);
          this.pdfFormDesigner?.loadConfiguration(config);
        } catch (error) {
          console.error('Error importing configuration:', error);
          this.notificationService.displayError('Invalid configuration file');
        }
      };
      reader.readAsText(file);
    }
  }

  /**
   * Clear all form elements
   */
  clearAllElements(): void {
    if (confirm(this.translate.instant('Are you sure you want to clear all form elements?'))) {
      this.pdfFormDesigner?.clearAllElements();
    }
  }

  /**
   * Toggle read-only mode
   */
  toggleReadOnlyMode(): void {
    this.formDesignerOptions = {
      ...this.formDesignerOptions,
      readOnly: !this.formDesignerOptions.readOnly
    };
  }

  // PDF viewer control methods
  onZoomIn(): void {
    this.pdfFormDesigner?.zoomIn();
  }

  onZoomOut(): void {
    this.pdfFormDesigner?.zoomOut();
  }

  onRotateClockwise(): void {
    this.pdfFormDesigner?.rotateClockwise();
  }

  onResetView(): void {
    this.pdfFormDesigner?.resetView();
  }

  onNextPage(): void {
    this.pdfFormDesigner?.nextPage();
  }

  onPreviousPage(): void {
    this.pdfFormDesigner?.previousPage();
  }

  // Event handlers for PDF viewer changes
  onZoomChange(zoom: number): void {
    this.currentZoom = zoom;
  }

  onRotationChange(rotation: number): void {
    this.currentRotation = rotation;
  }

  onPageChange(page: number): void {
    this.currentPage = page;
  }

  /**
   * Toggle sidebar visibility for mobile
   */
  showSidebar(): void {
    this.toogleSidebar = !this.toogleSidebar;

    if (this.toogleSidebar) {
      document.body.classList.add('sidebar-mobile-main');
    } else {
      document.body.classList.remove('sidebar-mobile-main');
    }
  }

  /**
   * Handle when external toolbar is ready
   */
  onToolbarReady(toolbarElement: HTMLElement): void {
    console.log('External toolbar ready:', toolbarElement);
    // Apply custom styling if needed
    toolbarElement.style.setProperty('--external-toolbar-bg', '#ffffff');
    toolbarElement.style.setProperty('--external-horizontal-gap', '8px');
  }
}
